import { Component } from '@angular/core';
import { TicketDownloadService } from '../../services';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  template: ''
})
export abstract class TicketDownloadBaseComponent {
  constructor(
    private _ticketDownloadService: TicketDownloadService,
    private _snackbar: MatSnackBar
  ) {}

  private ticketUrls = new Map<string, string>();
  private reservationTicketUrls = new Map<string, string[]>();
  private reservationZipUrls = new Map<string, string>();

  private reservationLoadingStates = new Map<string, boolean>();
  private ticketLoadingStates = new Map<string, boolean>();

  async fetchReservationTicketsPdfUrls(
    reservationId: string
  ): Promise<string[]> {
    if (this.reservationTicketUrls.get(reservationId))
      return this.reservationTicketUrls.get(reservationId) as string[];

    this.reservationLoadingStates.set(reservationId, true);
    try {
      const urls = await this._ticketDownloadService.getReservationPdfUrls(
        reservationId
      );
      this.reservationTicketUrls.set(reservationId, urls);

      return urls;
    } catch (e) {
      console.error(e);

      this._snackbar.open("Couldn't get the e-tickets");
      throw e;
    } finally {
      this.reservationLoadingStates.set(reservationId, false);
    }
  }

  async fetchReservationTicketsPdfZipUrl(
    reservationId: string
  ): Promise<string> {
    if (this.reservationZipUrls.get(reservationId))
      return this.reservationZipUrls.get(reservationId) as string;

    this.reservationLoadingStates.set(reservationId, true);
    try {
      const url = await this._ticketDownloadService.getReservationZipUrl(
        reservationId
      );
      this.reservationZipUrls.set(reservationId, url);

      return url;
    } catch (e) {
      console.error(e);

      this._snackbar.open("Couldn't get the e-tickets");
      throw e;
    } finally {
      this.reservationLoadingStates.set(reservationId, false);
    }
  }

  async fetchTicketPdfUrl(ticketId: string): Promise<string> {
    if (this.ticketUrls.get(ticketId))
      return this.ticketUrls.get(ticketId) as string;

    this.ticketLoadingStates.set(ticketId, true);
    try {
      const url = await this._ticketDownloadService.getTicketPdfUrl(ticketId);
      this.ticketUrls.set(ticketId, url);

      return url;
    } catch (e) {
      console.error(e);

      this._snackbar.open("Couldn't get the e-ticket");
      throw e;
    } finally {
      this.ticketLoadingStates.set(ticketId, false);
    }
  }

  hasTicketsForReservation(reservationId: string): boolean {
    return !!this.reservationTicketUrls.get(reservationId);
  }

  hasZipForReservation(reservationId: string): boolean {
    return !!this.reservationZipUrls.get(reservationId);
  }

  isFetchingTicketsForReservation(reservationId: string): boolean {
    return !!this.reservationLoadingStates.get(reservationId);
  }

  isFetchingTicketById(ticketId: string): boolean {
    return !!this.ticketLoadingStates.get(ticketId);
  }

  getTicketUrlsFetchedForReservation(reservationId: string): string[] {
    return this.reservationTicketUrls.get(reservationId) ?? [];
  }

  getTicketZipUrlFetchedForReservation(reservationId: string): string {
    return this.reservationZipUrls.get(reservationId) ?? '';
  }

  getTicketUrlFetchedForTicket(ticketId: string): string | null {
    return this.ticketUrls.get(ticketId) ?? null;
  }

  async onGetSingleTicketClicked(ticketId: string) {
    await this.fetchTicketPdfUrl(ticketId);

    this.onSaveSingleTicketClicked(ticketId);
  }

  async onSaveSingleTicketClicked(ticketId: string) {
    const url = this.getTicketUrlFetchedForTicket(ticketId);

    if (url) {
      window.open(url);
    }
  }

  async onGetTicketsClicked(reservationId: string) {
    await this.fetchReservationTicketsPdfZipUrl(reservationId);

    this.onSaveTicketsClicked(reservationId);
  }

  async onSaveTicketsClicked(reservationId: string) {
    const url = this.getTicketZipUrlFetchedForReservation(reservationId);

    window.open(url);
  }
}
